import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { useTypedNotification } from '../../hooks/useTypedNotification';
import { IntercomConversation, IntercomSettings, ConversationFilters, ConversationSyncStats } from '../../types/intercom';
import ConversationsList from './ConversationsList';
import ConversationDetails from './ConversationDetails';
import IntercomFilters from './IntercomFilters';

const IntercomView: React.FC = () => {
  const { showNotification } = useTypedNotification();
  const [settings, setSettings] = useState<IntercomSettings | null>(null);
  const [conversations, setConversations] = useState<IntercomConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<IntercomConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filters, setFilters] = useState<ConversationFilters>({});
  const [syncStats, setSyncStats] = useState<ConversationSyncStats | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchConversations();
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [filters]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/intercom/settings', {
        headers: { 'x-auth-token': token }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching Intercom settings:', error);
      showNotification('Failed to load Intercom settings', 'error');
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await axios.get(`/api/intercom/conversations?${params.toString()}`, {
        headers: { 'x-auth-token': token }
      });
      
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      showNotification('Failed to load conversations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const syncConversations = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/intercom/sync', {}, {
        headers: { 'x-auth-token': token }
      });
      
      setSyncStats(response.data.stats);
      showNotification(`Sync complete: ${response.data.stats.new_conversations} new conversations, ${response.data.stats.new_messages} new messages`, 'success');
      
      // Refresh conversations list
      await fetchConversations();
    } catch (error) {
      console.error('Error syncing conversations:', error);
      const axiosError = error as AxiosError<{ error?: string }>;
      showNotification(axiosError.response?.data?.error || 'Failed to sync conversations', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleConversationSelect = (conversation: IntercomConversation) => {
    setSelectedConversation(conversation);
  };

  const handleFiltersChange = (newFilters: ConversationFilters) => {
    setFilters(newFilters);
  };

  if (!settings || !settings.enabled) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Intercom Not Configured</h2>
            <p className="text-gray-600 mb-6">
              Intercom integration is not enabled or configured for your company.
            </p>
            <a
              href="/settings"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Configure Intercom
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Intercom Conversations</h1>
              <p className="text-gray-600 mt-1">
                View and manage customer conversations from Intercom
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {syncStats && (
                <div className="bg-white rounded-lg border border-gray-200 px-4 py-2">
                  <div className="text-xs text-gray-500">Last manual sync</div>
                  <div className="text-sm font-medium">
                    {syncStats.new_conversations} new, {syncStats.new_messages} messages
                  </div>
                </div>
              )}
              
              {/* Real-time status indicator */}
              <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live updates</span>
              </div>

              {/* Reduced prominence sync button */}
              <button
                onClick={syncConversations}
                disabled={syncing}
                className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                title="Manual sync for historical conversations or troubleshooting"
              >
                {syncing ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-2"></div>
                    Syncing...
                  </>
                ) : (
                  <>
                    <svg className="w-3 w-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Manual Sync
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <IntercomFilters filters={filters} onFiltersChange={handleFiltersChange} />
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className="lg:col-span-1">
            <ConversationsList
              conversations={conversations}
              selectedConversation={selectedConversation}
              onConversationSelect={handleConversationSelect}
              loading={loading}
            />
          </div>

          {/* Conversation Details */}
          <div className="lg:col-span-2">
            {selectedConversation ? (
              <ConversationDetails conversation={selectedConversation} />
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Conversation Selected</h3>
                <p className="text-gray-600">
                  Select a conversation from the list to view its details and messages.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntercomView;